import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/useApp.js';
import { 
  ArrowLeft,
  Calendar,
  Clock,
  User,
  Phone,
  Video,
  MapPin,
  CheckCircle,
  AlertCircle,
  FileText
} from 'lucide-react';

// Helper function to get user-specific booking keys
const getUserBookingKeys = () => {
  const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
  const userId = currentUser.uid || currentUser.id || 'anonymous';
  return {
    userId,
    userBookingsKey: `userBookings_${userId}`,
    generalBookingsKey: `bookings_${userId}`
  };
};

const LawyerRescheduleAppointment = () => {
  const navigate = useNavigate();
  const { language } = useApp();
  const [appointments, setAppointments] = useState([]);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('');
  const [showRescheduleForm, setShowRescheduleForm] = useState(false);
  const [loading, setLoading] = useState(false);

  const translations = {
    english: {
      rescheduleAppointment: 'Reschedule Appointment',
      back: 'Back',
      selectAppointment: 'Select Appointment to Reschedule',
      noAppointments: 'No appointments found to reschedule',
      clientName: 'Client Name',
      date: 'Date',
      time: 'Time',
      type: 'Type',
      caseType: 'Case Type',
      amount: 'Amount',
      status: 'Status',
      reschedule: 'Reschedule',
      cancel: 'Cancel',
      newDate: 'New Date',
      newTime: 'New Time',
      confirmReschedule: 'Confirm Reschedule',
      appointmentRescheduled: 'Appointment Rescheduled Successfully!',
      notificationSent: 'Notification sent to client',
      originalDate: 'Original Date',
      originalTime: 'Original Time',
      rescheduledDate: 'Rescheduled Date',
      rescheduledTime: 'Rescheduled Time',
      videoCall: 'Video Call',
      phoneCall: 'Phone Call',
      inPerson: 'In Person',
      scheduled: 'Scheduled',
      rescheduled: 'Rescheduled',
      completed: 'Completed',
      cancelled: 'Cancelled'
    },
    hindi: {
      rescheduleAppointment: 'नियुक्ति पुनर्निर्धारित करें',
      back: 'वापस',
      selectAppointment: 'पुनर्निर्धारित करने के लिए नियुक्ति चुनें',
      noAppointments: 'पुनर्निर्धारित करने के लिए कोई नियुक्ति नहीं मिली',
      clientName: 'ग्राहक का नाम',
      date: 'तारीख',
      time: 'समय',
      type: 'प्रकार',
      caseType: 'मामले का प्रकार',
      amount: 'राशि',
      status: 'स्थिति',
      reschedule: 'पुनर्निर्धारित करें',
      cancel: 'रद्द करें',
      newDate: 'नई तारीख',
      newTime: 'नया समय',
      confirmReschedule: 'पुनर्निर्धारण की पुष्टि करें',
      appointmentRescheduled: 'नियुक्ति सफलतापूर्वक पुनर्निर्धारित की गई!',
      notificationSent: 'ग्राहक को सूचना भेजी गई',
      originalDate: 'मूल तारीख',
      originalTime: 'मूल समय',
      rescheduledDate: 'पुनर्निर्धारित तारीख',
      rescheduledTime: 'पुनर्निर्धारित समय',
      videoCall: 'वीडियो कॉल',
      phoneCall: 'फोन कॉल',
      inPerson: 'व्यक्तिगत',
      scheduled: 'निर्धारित',
      rescheduled: 'पुनर्निर्धारित',
      completed: 'पूर्ण',
      cancelled: 'रद्द'
    }
  };

  const t = translations[language] || translations.english;

  useEffect(() => {
    loadAppointments();
  }, []);

  const loadAppointments = () => {
    const existingAppointments = JSON.parse(localStorage.getItem('lawyer_appointments') || '[]');
    setAppointments(existingAppointments);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'text-blue-600 bg-blue-50';
      case 'rescheduled': return 'text-orange-600 bg-orange-50';
      case 'completed': return 'text-green-600 bg-green-50';
      case 'cancelled': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Video Call': return <Video className="w-4 h-4" />;
      case 'Phone Call': return <Phone className="w-4 h-4" />;
      case 'In Person': return <MapPin className="w-4 h-4" />;
      default: return <Calendar className="w-4 h-4" />;
    }
  };

  const handleReschedule = (appointment) => {
    setSelectedAppointment(appointment);
    setNewDate(appointment.date);
    setNewTime(appointment.time);
    setShowRescheduleForm(true);
  };

  const handleConfirmReschedule = () => {
    if (!newDate || !newTime) {
      alert('Please enter both date and time');
      return;
    }

    setLoading(true);

    // Update lawyer appointments
    const updatedLawyerAppointments = appointments.map(apt => {
      if (apt.id === selectedAppointment.id) {
        return {
          ...apt,
          date: newDate,
          time: newTime,
          status: 'rescheduled',
          originalDate: apt.date,
          originalTime: apt.time,
          rescheduledAt: new Date().toISOString()
        };
      }
      return apt;
    });

    localStorage.setItem('lawyer_appointments', JSON.stringify(updatedLawyerAppointments));

    // Update client bookings
    const { userBookingsKey, generalBookingsKey } = getUserBookingKeys();
    const clientBookings = JSON.parse(localStorage.getItem(generalBookingsKey) || '[]');
    const updatedClientBookings = clientBookings.map(booking => {
      if (booking.clientName === selectedAppointment.clientName && 
          booking.date === selectedAppointment.originalDate && 
          booking.time === selectedAppointment.originalTime) {
        return {
          ...booking,
          date: newDate,
          time: newTime,
          status: 'rescheduled',
          originalDate: booking.date,
          originalTime: booking.time,
          rescheduledAt: new Date().toISOString(),
          rescheduledBy: 'lawyer'
        };
      }
      return booking;
    });

    localStorage.setItem(generalBookingsKey, JSON.stringify(updatedClientBookings));

    // Add to lawyer cases
    const lawyerCases = JSON.parse(localStorage.getItem('lawyer_cases') || '[]');
    const rescheduleCase = {
      id: Date.now(),
      clientName: selectedAppointment.clientName,
      caseType: selectedAppointment.caseType,
      status: 'rescheduled',
      originalDate: selectedAppointment.date,
      originalTime: selectedAppointment.time,
      newDate: newDate,
      newTime: newTime,
      type: selectedAppointment.type,
      amount: selectedAppointment.amount,
      rescheduledAt: new Date().toISOString(),
      rescheduledBy: 'lawyer'
    };

    lawyerCases.push(rescheduleCase);
    localStorage.setItem('lawyer_cases', JSON.stringify(lawyerCases));

    setTimeout(() => {
      setLoading(false);
      setShowRescheduleForm(false);
      setSelectedAppointment(null);
      loadAppointments();
      
      alert(`${t.appointmentRescheduled}\n\n${t.clientName}: ${selectedAppointment.clientName}\n${t.rescheduledDate}: ${newDate}\n${t.rescheduledTime}: ${newTime}\n\n${t.notificationSent}`);
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-4"
      >
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/lawyer-dashboard')}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              {t.rescheduleAppointment}
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t.selectAppointment}
            </p>
          </div>
        </div>
      </motion.div>

      {/* Main Content */}
      <div className="px-4 py-6 space-y-6">
        {appointments.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-12"
          >
            <AlertCircle className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {t.noAppointments}
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              No appointments are currently available for rescheduling.
            </p>
          </motion.div>
        ) : (
          <div className="space-y-4">
            {appointments.map((appointment, index) => (
              <motion.div
                key={appointment.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/20 rounded-lg flex items-center justify-center">
                      <User className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {appointment.clientName}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {appointment.caseType}
                      </p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(appointment.status)}`}>
                    {t[appointment.status] || appointment.status}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {appointment.date}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {appointment.time}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {getTypeIcon(appointment.type)}
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {t[appointment.type.replace(' ', '').toLowerCase()] || appointment.type}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-medium text-green-600">
                      {appointment.amount}
                    </span>
                  </div>
                </div>

                {appointment.status === 'scheduled' && (
                  <button
                    onClick={() => handleReschedule(appointment)}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white py-2 px-4 rounded-lg font-medium transition-colors"
                  >
                    {t.reschedule}
                  </button>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Reschedule Modal */}
      {showRescheduleForm && selectedAppointment && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-md"
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {t.confirmReschedule}
            </h3>

            <div className="space-y-4">
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  {t.clientName}: <span className="font-medium text-gray-900 dark:text-white">{selectedAppointment.clientName}</span>
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  {t.originalDate}: <span className="font-medium text-gray-900 dark:text-white">{selectedAppointment.date}</span>
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t.originalTime}: <span className="font-medium text-gray-900 dark:text-white">{selectedAppointment.time}</span>
                </p>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t.newDate}
                  </label>
                  <input
                    type="text"
                    value={newDate}
                    onChange={(e) => setNewDate(e.target.value)}
                    placeholder="DD/MM/YYYY"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t.newTime}
                  </label>
                  <input
                    type="text"
                    value={newTime}
                    onChange={(e) => setNewTime(e.target.value)}
                    placeholder="HH:MM AM/PM"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowRescheduleForm(false)}
                  className="flex-1 py-2 px-4 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  {t.cancel}
                </button>
                <button
                  onClick={handleConfirmReschedule}
                  disabled={loading}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      {t.confirmReschedule}
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default LawyerRescheduleAppointment;
